/* eslint-disable @typescript-eslint/no-unused-vars */
import { Avatar, Button, CloseButton, Flex, Grid, Input, NumberInput, Select, SelectProps, Text } from "@mantine/core"
import { useForm } from "@mantine/form"
import { ImageIcon } from "@phosphor-icons/react";
import { useState } from "react"
import { useTranslation } from "react-i18next"
import useSwr from "swr"
import * as yup from "yup"
import { yupResolver } from "mantine-form-yup-resolver"
import { i18n } from "@dehub/theme"
import { GoodsApi } from "../../apis"
import { currencyFormat } from "../../utils"
import { message } from "../../utils/message"
import { ModalFooter, PageLayout } from "../layout"

const schema = yup.object({
  variantId: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
  quantity: yup.number().min(1, () => i18n.t("common:validation.required")).typeError(() => i18n.t("common:validation.required")).required(() => i18n.t("common:validation.required")).nullable(),
})

export function SupplierSalesAddDynamicAdditionalForm({ action, setFieldValue, setAction, values }: any) {
  const { t } = useTranslation(["supplierSales", "common"])
  const [query, setQuery] = useState<string | any>("")
  const [loading, setLoading] = useState<boolean>(false)

  const { data: variantList = [] } = useSwr(
    `/erp/invoice/variant/list?query=${query}`,
    async () =>
      await GoodsApi.variant({
        filter: {
          query: "",
          categoryId: "",
          brandId: "",
          orderType: "SALES",
        },
        offset: {
          page: 1,
          limit: 100,
        },
        businessId: values?.types ? values?.receiverBusinessId : values?.supplierId,
      }),
    {
      fallback: [],
    },
  )

  const units = variantList?.rows?.flatMap((vl: any) =>
    vl?.prices.map((price: any) => ({
      label: price.unitName,
      value: price.unitId,
    })),
  )

  const [data] = useState<any>({
    id: undefined,
    barCode: undefined,
    brand: undefined,
    category: undefined,
    erpCode: undefined,
    hasTax: undefined,
    hasVat: undefined,
    image: undefined,
    nameApp: undefined,
    nameBill: undefined,
    nameEng: undefined,
    nameWeb: undefined,
    price: undefined,
    priceStartDate: undefined,
    refCode: undefined,
    skuCode: undefined,
    supplier: undefined,
    taxAmount: undefined,
    unit: undefined,
    vatAmount: undefined,
    inventories: undefined,
    prices: undefined,

    ...(action[0] === "edit" && action[1]),
  })

  const form = useForm<any>({
    mode: "controlled",
    initialValues: data,
    validate: yupResolver(schema),
  })

  const onSearchService = async (query: any) => {
    let timeout
    if (query) {
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(async () => {
        setQuery(query)
      }, 1000)
    } else {
      setQuery("")
    }
  }

  const getVariant = async (id: string) => {
    let res = null

    try {
      res = variantList.rows.filter((item: any) => item?.id === id)[0]
      form.setFieldValue("id", res.id)
      form.setFieldValue("barCode", res.barCode)
      form.setFieldValue("brand", res.brand)
      form.setFieldValue("category", res.category)
      form.setFieldValue("erpCode", res.erpCode)
      form.setFieldValue("hasTax", res.hasTax)
      form.setFieldValue("hasVat", res.hasVat)
      form.setFieldValue("image", res.image)
      form.setFieldValue("nameApp", res.nameApp)
      form.setFieldValue("nameBill", res.nameBill)
      form.setFieldValue("nameEng", res.nameEng)
      form.setFieldValue("nameWeb", res.nameWeb)
      form.setFieldValue("price", res.price)
      form.setFieldValue("priceStartDate", res.priceStartDate)
      form.setFieldValue("refCode", res.refCode)
      form.setFieldValue("skuCode", res.skuCode)
      form.setFieldValue("supplier", res.supplier)
      form.setFieldValue("taxAmount", res.taxAmount)
      form.setFieldValue("unit", res.unit)
      form.setFieldValue("vatAmount", res.vatAmount)
      form.setFieldValue("inventories", res.inventories)
      form.setFieldValue("prices", res.prices)
      form.setFieldValue("vatPercent", res.vatPercent)
      form.setFieldValue("taxPercent", res.taxPercent)
    } catch (error) {}
  }

  const onSubmit = async (formValues: any) => {
    setLoading(true)

    if (action[1]?.lines?.some((item: any) => item.id === formValues.id)) {
      message.error(t("supplierSales:sales.productAlreadyAdded"))
    } else {
      setFieldValue("lines", [...(action[1]?.lines || []), formValues])
      setAction([])
    }

    setLoading(false)
  }

  const renderSelectOption: SelectProps["renderOption"] = ({ option }: { option: any }) => {
    return (
      <Flex align="center" gap="xs">
        <Avatar src={option?.item?.image} size={40}>
          <ImageIcon />
        </Avatar>
        {option.label}
      </Flex>
    )
  }

  return (
    <PageLayout title={t("supplierSales:sales.addProduct")} subTitle={t("supplierSales:sales.addProductSubTitle")} extra={[<CloseButton onClick={() => setAction([])} key={1}></CloseButton>]}>
      <Grid>
        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
          <Select
            {...form.getInputProps("variantId")}
            renderOption={renderSelectOption}
            searchable
            clearable
            placeholder={t("supplierSales:sales.productService")}
            label={t("supplierSales:sales.productService")}
            onSearchChange={(e) => onSearchService(e)}
            onChange={(e: any) => {
              form.setFieldValue("variantId", e)
              form.setFieldValue("quantity", undefined)
              getVariant(e)
            }}
            data={(variantList.rows ?? []).map((item: any) => {
              return {
                label: `${item.nameMon} ${item.optionValueNames ? " - " + item.optionValueNames : ""}`,
                value: item.id,
                item: item,
              }
            })}
            required
            withAsterisk
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
          <Select
            {...form.getInputProps("variantId")}
            placeholder={t("supplierSales:sales.selectUnit")}
            label={t("supplierSales:sales.unit")}
            onSearchChange={(e) => onSearchService(e)}
            onChange={(value: string | null) => {
              form.setFieldValue("variantId", value)
              getVariant(value!)
              form.setFieldValue("unitId", value)
            }}
            data={units ?? []}
            required
            withAsterisk
            searchable
            clearable
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
          <NumberInput
            {...form.getInputProps("quantity")}
            min={0}
            placeholder={t("supplierSales:sales.quantity")}
            label={t("supplierSales:sales.quantity")}
            required
            withAsterisk
            hideControls
            disabled={!form.values?.variantId}
            onChange={(val: number | string) => {
              const numVal = typeof val === "number" ? val : Number(val)
              if (!numVal || numVal < 0) return
              form.setFieldValue("quantity", numVal)
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
          <Input.Wrapper label={`${t("supplierSales:sales.unitPrice")}  /${form.values?.unit || t("supplierSales:sales.piece")}/`}>
            <Flex align={"center"} h="36px">
              <Text size="sm" fw={500} c="var(--mantine-color-dark-5)">
                {currencyFormat(form.values?.price || 0)}
              </Text>
            </Flex>
          </Input.Wrapper>
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
          <Input.Wrapper label={t("supplierSales:sales.totalPrice")}>
            <Flex align={"center"} h="36px">
              <Text size="sm" fw={500} c="var(--mantine-color-dark-5)">
                {currencyFormat((form.values?.price || 0) * (form.values?.quantity || 0))}
              </Text>
            </Flex>
          </Input.Wrapper>
        </Grid.Col>
      </Grid>
      <ModalFooter>
        <Button variant="default" onClick={() => setAction([])}>
          {t("common:actions.back")}
        </Button>
        <Button
          loading={loading}
          disabled={!form.values?.variantId}
          onClick={async () => {
            const { hasErrors } = form.validate()
            if (!hasErrors) onSubmit(form.values)
          }}>
          {t("common:actions.save")}
        </Button>
      </ModalFooter>
    </PageLayout>
  )
}
